import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Drawer } from "../../../components/Drawer";
import { SearchInputEntries } from "../../../components/InputSearchEntries";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import i18n from "../../../i18n";
import { CreateKanbanCardDto } from "../../../model/kanban/dto/CreateKanbanCardDto";
import { entryActions } from "../../../redux/entry/entry.slice";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import {
  getTranslatedKanbanColumnName,
  getTranslatedKanbanName,
} from "../KanbanUtils";
import { AtiraDatePicker } from "../../../components/AtiraDatePicker";
import { useDrawersContext } from "../../tasks/drawers-context";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { TestIDs } from "../../../tests/types/testIds";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { getCurrencySymbol } from "../../../utils/pricingUtils";

const StyledSelect = styled(AtiraSelect)`
  height: 2.3rem;
`;

const StyledInput = styled(Input)`
  height: 2.3rem;
  direction: ${i18n.language === AppLangs.AR ? "rtl" : "ltr"};

  &::placeholder {
    font-size: 1.5rem;
  }
`;

export const KanbanCardCreateDrawer: React.FC = () => {
  const [kanbanCardCreateLoading, setKanbanCardCreateLoading] = useState(false);

  const { t } = useTranslation();

  const {
    selectedKanbanColumnId,
    setSelectedKanbanColumnId,
    entry,
    kanbanCardCreateVisible,
    setKanbanCardCreateVisible,
    selectedEntryForKanbanCard,
    setSelectedEntryForKanbanCard,
  } = useDrawersContext();
  const dispatch = useAppDispatch();

  const { control, handleSubmit, getValues, setValue, reset, watch, setError } =
    useForm<
      Omit<CreateKanbanCardDto, "columnId"> & { columnId: string | null }
    >();

  const kanbans = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const userKanban = useAppSelector(
    kanbanSliceSelectors.selectUserCurrentKanban,
  );
  const selectedKanbanId = watch("kanbanId") || userKanban?._id;
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);

  const values = watch();

  const onCreateKanbanCrad = async () => {
    try {
      setKanbanCardCreateLoading(true);

      const dto = getValues();

      const payload = omitBy(
        {
          userId,
          entryId: dto.entryId,
          kanbanId: dto.kanbanId,
          columnId: dto.columnId,
          order: 0,
          title: dto.title,
          closingDate: dto.closingDate ? new Date(dto.closingDate) : null,
          amount: dto.amount,
          note: dto.note || undefined,
        },
        isNil,
      ) as unknown as CreateKanbanCardDto;

      await dispatch(kanbanActions.createKanbanCard(payload)).unwrap();
      // Fetch current on purpose
      // The user can change the kanban after creating a card
      await dispatch(
        kanbanActions.getUserKanban({
          userId,
          kanbanId: userKanban?._id,
        }),
      ).unwrap();

      localOnClose();
      AtiraToast.success(t("deals.drawer.create.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setKanbanCardCreateLoading(false);
    }
  };

  const localOnClose = () => {
    setKanbanCardCreateVisible(false);
    setSelectedEntryForKanbanCard(null);
    reset();
    if (selectedKanbanColumnId) {
      setSelectedKanbanColumnId(null);
    }
  };

  const onFetchEntries = async (keyword: string) => {
    try {
      const entries = await dispatch(
        entryActions.getEntriesLegibleForKanbanCard({
          keyword,
          userId,
        }),
      ).unwrap();

      return entries || [];
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      return [];
    }
  };

  useEffect(() => {
    if (kanbanCardCreateVisible) {
      reset({
        title: "",
        note: "",
        closingDate: undefined,
        amount: "0",
        kanbanId: userKanban?._id,
        columnId:
          selectedKanbanColumnId ||
          userKanban?.columns?.find((c) => c.order === 0)?._id,
        entryId: entry?._id,
        userId,
      });
      dispatch(entryActions.getEntriesLegibleForKanbanCard({ userId }));
    }
  }, [
    dispatch,
    kanbanCardCreateVisible,
    selectedKanbanColumnId,
    reset,
    userId,
    userKanban?._id,
    userKanban?.columns,
    entry?._id,
  ]);

  const getIsButtonDisabled = () => {
    if (!values?.title || !values?.entryId || !values?.columnId) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (entry && kanbanCardCreateVisible) {
      setValue("entryId", entry._id);
    }
  }, [entry, kanbanCardCreateVisible, setValue]);

  useEffect(() => {
    if (kanbanCardCreateVisible) {
      // Refetch on open so a column renamed/added since the last open shows up.
      dispatch(kanbanActions.getUserKanbansMeta({ userId }));
    }
  }, [dispatch, kanbanCardCreateVisible, userId]);

  return (
    <Drawer
      destroyOnHidden
      title={t("deals.drawer.create.title")}
      open={kanbanCardCreateVisible}
      onClose={localOnClose}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onCreateKanbanCrad)}
          onCancel={localOnClose}
          config={{
            loading: kanbanCardCreateLoading,
            disabled: getIsButtonDisabled(),
          }}
          submitTestId={TestIDs.entries.table.menu.deal.drawer.button}
        />
      }
      maskClosable={false}
      size="large"
    >
      <Flex flexDirection="column" gap="m">
        <Controller
          name="title"
          control={control}
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
            minLength: {
              value: Lengths.MIN_3,
              message: t("error.length.more", {
                name: t("deals.drawer.input.deal_name"),
                length: Lengths.MIN_3,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledInput
              required
              value={value}
              onChange={onChange}
              title={t("deals.drawer.input.deal_name")}
              valid={!error}
              errorMessage={error?.message}
              dataTestId={TestIDs.entries.table.menu.deal.drawer.name}
            />
          )}
        />

        <SearchInputEntries
          fetchOptions={onFetchEntries}
          onChange={(v) => setValue("entryId", v?._id || "")}
          title={t("deals.drawer.input.contact_name")}
          placeholder={t("deals.drawer.input.contact_name_placeholder")}
          required
          isSearchVisible={kanbanCardCreateVisible}
          searchTestId={
            TestIDs.entries.table.menu.deal.drawer.selectContact.input
          }
          resultItemTestId={
            TestIDs.entries.table.menu.deal.drawer.selectContact.option
          }
          defaultSelectedEntry={selectedEntryForKanbanCard}
        />

        <Controller
          name="kanbanId"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
          }}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <StyledSelect
              value={value}
              options={kanbans.map(({ _id, name }) => ({
                label: getTranslatedKanbanName(name),
                value: _id,
                "data-testid":
                  TestIDs.entries.table.menu.deal.drawer.kanban.option(
                    String(_id),
                  ),
              }))}
              onChange={(e) => {
                onChange(e);
                setValue("columnId", null);
              }}
              title={t("common.kanban")}
              valid={!error}
              errorMessage={error?.message}
              required
              className={TestIDs.entries.table.menu.deal.drawer.kanban.open}
            />
          )}
        />

        <Controller
          name="columnId"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
          }}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const selectedKanban =
              kanbans.find((k) => k._id === selectedKanbanId) || userKanban;
            const columns = [
              ...(selectedKanban?.columns?.filter((c) => c.default === false) ||
                []),
            ].sort((a, b) => a.order - b.order);

            return (
              <StyledSelect
                value={value}
                options={columns.map(({ _id, name }) => {
                  return {
                    label: getTranslatedKanbanColumnName(
                      name,
                      selectedKanban?.template || userKanban?.template,
                    ),
                    value: _id,
                    "data-testid":
                      TestIDs.entries.table.menu.deal.drawer.stage.option(
                        String(_id),
                      ),
                  };
                })}
                onChange={onChange}
                title={t("common.stage")}
                valid={!error}
                errorMessage={error?.message}
                required
                className={TestIDs.entries.table.menu.deal.drawer.stage.open}
              />
            );
          }}
        />

        <Flex position="relative">
          <Controller
            name="amount"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <StyledInput
                value={value}
                onChange={onChange}
                title={`${t("common.amount")} (${getCurrencySymbol(
                  user?.settings?.currency || "USD",
                )})`}
                type="number"
                valid={!error}
                errorMessage={error?.message}
                dataTestId={TestIDs.entries.table.menu.deal.drawer.amount}
              />
            )}
          />
        </Flex>

        <Controller
          name="closingDate"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AtiraDatePicker
              title={t("deals.drawer.input.closing_date")}
              onChange={onChange}
              value={value || null}
              valid={!error}
              showTime
              fromToday
              dataTestId={TestIDs.entries.table.menu.deal.drawer.closingDate}
            />
          )}
        />

        <Controller
          name="note"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledInput
              value={value}
              onChange={onChange}
              title={t("common.note")}
              type="text"
              valid={!error}
              errorMessage={error?.message}
              dataTestId={TestIDs.entries.table.menu.deal.drawer.note}
            />
          )}
        />
      </Flex>
    </Drawer>
  );
};
